import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import {
  Zap,
  Users,
  Globe,
  TrendingUp,
  Mail,
} from 'lucide-react';

// ============================================================================
// DATA
// ============================================================================


const CULTURE = [
  {
    icon: Zap,
    title: 'Ship fast, learn faster',
    desc: 'We move at startup speed — no bureaucracy, no waiting.',
  },
  {
    icon: Users,
    title: 'Work with the best',
    desc: "One Team. Limitless Possibilities. You'll level up fast.",
  },
  {
    icon: Globe,
    title: 'Flexible Hours',
    desc: 'Flexible work culture.',
  },
  {
    icon: TrendingUp,
    title: 'Grow with us',
    desc: 'Early team means real ownership and career trajectory.',
  },
];


// ============================================================================
// ANIMATION VARIANTS
// ============================================================================

const heroContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const listVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SectionEyebrow = ({ label }) => (
  <div className="careers-eyebrow">
    <span className="careers-eyebrow-line" />
    <span className="careers-eyebrow-text">{label}</span>
    <span className="careers-eyebrow-line" />
  </div>
);


// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CareersPage = () => {
  const applyRef = useRef(null);
  const cultureRef = useRef(null);
  const cultureInView = useInView(cultureRef, { once: true, margin: '-80px' });
  const formSectionRef = useRef(null);
  const formInView = useInView(formSectionRef, { once: true, margin: '-80px' });

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://monkstack.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Careers',
        item: 'https://monkstack.com/careers',
      },
    ],
  };

  return (
    <div className="App">
      <SEO
        title="Careers"
        description="Join monkStack in Pune. We are a small team of engineers, designers, and builders. Connect at info@monkstack.com."
        url="https://monkstack.com/careers"
        structuredData={breadcrumbSchema}
      />
      <Navbar />

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="careers-hero">
        <div className="container">
          <motion.div
            className="careers-hero-inner"
            initial="hidden"
            animate="visible"
            variants={heroContainerVariants}
          >
            <motion.div variants={fadeUp}>
              <SectionEyebrow label="Careers" />
            </motion.div>

            <motion.h1 className="careers-headline" variants={fadeUp}>
              Build the future, Join our team
            </motion.h1>

            <motion.p className="careers-subtext" variants={fadeUp}>
              We're a small, sharp team of builders — engineers, designers, and thinkers. If you care about craft, move fast, and want direct ownership of what you ship you'll fit right in.
            </motion.p>

            <motion.p className="careers-subtext careers-connect-text" variants={fadeUp}>
              Feel free to connect at{' '}
              <a href="mailto:info@monkstack.com" className="careers-email-link">
                info@monkstack.com
              </a>
            </motion.p>

          </motion.div>
        </div>
      </section>

      {/* ── OPEN ROLES ────────────────────────────────────────────────── */}
      {/* <section className="careers-roles-section" ref={rolesRef}>
        <div className="container" ref={rolesInViewRef}>
          <motion.div
            initial="hidden"
            animate={rolesInView ? 'visible' : 'hidden'}
            variants={fadeUp}
          >
            <SectionEyebrow label="Open Roles" />
          </motion.div>

          <motion.h2
            className="careers-section-heading"
            initial="hidden"
            animate={rolesInView ? 'visible' : 'hidden'}
            variants={fadeUp}
          >
            Current openings
          </motion.h2>

          <motion.div
            initial="hidden"
            animate={rolesInView ? 'visible' : 'hidden'}
            variants={listVariants}
          >
            {ROLES.map((role) => (
              <RoleCard key={role.id} role={role} onApply={handleApply} />
            ))}
          </motion.div>
        </div>
      </section> */}

      {/* ── CULTURE ───────────────────────────────────────────────────── */}
      <section className="careers-culture-section" ref={cultureRef}>
        <div className="container">
          <motion.div
            className="careers-culture-grid"
            initial="hidden"
            animate={cultureInView ? 'visible' : 'hidden'}
            variants={listVariants}
          >
            {CULTURE.map((item) => {
              const Icon = item.icon;
              return (
                <motion.div key={item.title} className="careers-culture-item" variants={itemVariants}>
                  <div className="careers-culture-icon-box">
                    <Icon size={22} className="careers-culture-icon" aria-hidden="true" />
                  </div>
                  <h3 className="careers-culture-title">{item.title}</h3>
                  <p className="careers-culture-desc">{item.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

{/* ── APPLICATION FORM ──────────────────────────────────────────── */}
      <section id="apply" className="careers-apply-section" ref={applyRef}>
        <div className="container" ref={formSectionRef}>
          <motion.div
            initial="hidden"
            animate={formInView ? 'visible' : 'hidden'}
            variants={fadeUp}
          >
            <SectionEyebrow label="Apply" />
          </motion.div>

          <motion.h2
            className="careers-section-heading"
            initial="hidden"
            animate={formInView ? 'visible' : 'hidden'}
            variants={fadeUp}
          >
            Apply to join monkstack
          </motion.h2>

          <motion.p
            className="careers-apply-subtext"
            initial="hidden"
            animate={formInView ? 'visible' : 'hidden'}
            variants={fadeUp}
          >
            Reach us directly at our email — we'll get back within 24 hours.
          </motion.p>

          <motion.div
            initial="hidden"
            animate={formInView ? 'visible' : 'hidden'}
            variants={fadeUp}
          >
            <a href="mailto:info@monkstack.com" className="careers-email-cta">
              <Mail size={18} aria-hidden="true" />
              info@monkstack.com
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CareersPage;
